import React from 'react';
import { ArrowRight, Sparkle, Clock } from '@phosphor-icons/react';

/**
 * Sprint C — ThisWeeksBestLesson
 * Source: /api/university/topics → weekly_lesson
 * Schema: { topic_id, title, summary, why_now, type, benefit_category, estimated_minutes }
 */
const BENEFIT_TONE = {
  Energy: { ring: 'border-amber-200', dot: 'bg-amber-400', text: 'text-amber-700' },
  Sleep: { ring: 'border-indigo-200', dot: 'bg-indigo-400', text: 'text-indigo-700' },
  Mood: { ring: 'border-rose-200', dot: 'bg-rose-400', text: 'text-rose-700' },
  Clarity: { ring: 'border-sky-200', dot: 'bg-sky-400', text: 'text-sky-700' },
  Safety: { ring: 'border-emerald-200', dot: 'bg-emerald-400', text: 'text-emerald-700' },
  Confidence: { ring: 'border-[#E5DFD0]', dot: 'bg-[#235347]', text: 'text-[#235347]' },
};

export const ThisWeeksBestLesson = ({ lesson, onStart }) => {
  if (!lesson || !lesson.title) return null;
  const tone = BENEFIT_TONE[lesson.benefit_category] || BENEFIT_TONE.Confidence;
  const minutes = lesson.estimated_minutes || 5;

  return (
    <section
      data-testid="weekly-lesson-hero"
      className="relative rounded-2xl bg-[#0E1C2E] text-white overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-[0.08] pointer-events-none"
        style={{
          backgroundImage:
            'radial-gradient(circle at 20% 0%, #2FB3A0 0%, transparent 40%), radial-gradient(circle at 80% 100%, #CDA873 0%, transparent 40%)',
        }}
      />
      <div className="relative p-6 sm:p-8 lg:p-10">
        <div className="flex items-center gap-2 mb-4">
          <Sparkle size={12} weight="fill" className="text-[#CDA873]" aria-hidden />
          <span className="text-[10px] font-semibold uppercase tracking-[0.22em] text-white/70">
            This week's best lesson
          </span>
        </div>

        <h2
          data-testid="weekly-lesson-title"
          className="font-serif text-[24px] sm:text-[28px] lg:text-[32px] leading-[1.18] tracking-[-0.012em] text-white font-normal"
        >
          {lesson.title}
        </h2>

        {lesson.summary && (
          <p className="text-[13.5px] sm:text-[14px] text-white/75 mt-3 leading-relaxed max-w-xl line-clamp-3">
            {lesson.summary}
          </p>
        )}

        {lesson.why_now && (
          <div className="mt-5 pt-5 border-t border-white/10 max-w-xl">
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-white/50 mb-1.5">
              Why for you
            </p>
            <p
              data-testid="weekly-lesson-why"
              className="text-[13px] italic text-white/80 leading-relaxed"
            >
              {lesson.why_now}
            </p>
          </div>
        )}

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => onStart && onStart(lesson)}
            data-testid="weekly-lesson-start"
            className="inline-flex items-center gap-2 bg-white text-[#0E1C2E] text-[14px] font-semibold px-5 py-3 rounded-lg hover:bg-[#F4EFE3] transition-colors group"
          >
            Start in {minutes} min
            <ArrowRight size={14} weight="bold" className="transition-transform group-hover:translate-x-0.5" />
          </button>
          <div
            data-testid="weekly-lesson-benefit"
            className={`inline-flex items-center gap-2 bg-white/8 border ${tone.ring} rounded-full px-3 py-1.5`}
          >
            <span className={`w-1.5 h-1.5 rounded-full ${tone.dot}`} aria-hidden />
            <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-white/80">
              Estimated benefit · {lesson.benefit_category}
            </span>
          </div>
          <span className="inline-flex items-center gap-1 text-[11px] text-white/55">
            <Clock size={11} weight="duotone" />
            {minutes} min
          </span>
        </div>
      </div>
    </section>
  );
};

export default ThisWeeksBestLesson;
